import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CalendarDays,
  GraduationCap,
  User,
  Check,
  AlertCircle,
  Printer,
  Star,
} from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';

import { fetchUserRecord } from '../../../utils/mongoDataOperations';
import {
  formatDate,
  calculateAge,
  checkInHackerHelper,
} from '../../../utils/helpers';
import { printBadge } from '../../../utils/badgePrint';

const CheckIn = ({ user }) => {
  const [verificationState, setVerificationState] = useState({
    nameVerified: false,
    dobVerified: false,
    universityVerified: false,
  });
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scanKey, setScanKey] = useState(0); // increments on every scan to force re-fetch even for same ID
  const [scannedUserRecord, setScannedUserRecord] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [checkInIsLoading, setCheckInIsLoading] = useState(false);
  const [autoPrint, setAutoPrint] = useState(false);
  const autoPrintRef = useRef(false);

  // Wrap setter so every scan bumps the key, even if the ID is unchanged
  const handleScannedID = useCallback((id) => {
    setScannedReturnedID(id);
    setScanKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          setStatusMessage(null);
          setVerificationState({
            nameVerified: false,
            dobVerified: false,
            universityVerified: false,
          });

          const response = await fetchUserRecord(scannedReturnedID);

          setScannedUserRecord(response);

          // Auto-queue for laser badge if not yet checked in
          if (response && !response?.check_in_info?.checkedIn) {
            fetch('/api/laser-queue', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: scannedReturnedID,
                firstName: response?.first_name || '',
                lastName: response?.last_name || '',
              }),
            }).catch((err) =>
              console.warn('Failed to add to laser queue:', err)
            );
          }
        } catch (error) {
          console.error('Error fetching user record:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID, scanKey]);

  useEffect(() => {
    if (statusMessage) {
      setVerificationState({
        nameVerified: false,
        dobVerified: false,
        universityVerified: false,
      });

      // const timer = setTimeout(() => {
      //   setStatusMessage(null);
      // }, 5000);

      // return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const allVerified = Object.values(verificationState).every((value) => value);

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const VerificationCheckbox = ({ label, checked, onChange, instruction }) => (
    <div className='mb-4'>
      <label className='cursor-pointer flex items-start space-x-3'>
        <div className='flex h-5 items-center'>
          <input
            type='checkbox'
            checked={checked}
            onChange={onChange}
            className='border-gray-300 focus:ring-blue-500 h-4 rounded text-blue-600 w-4'
          />
        </div>
        <div className='flex flex-col'>
          <span className='font-medium'>{label}</span>
          <span className='text-gray-500 text-sm'>{instruction}</span>
        </div>
      </label>
    </div>
  );

  const StatusMessage = ({ type, text }) => (
    <div
      className={`flex items-center space-x-2 p-4 rounded-lg mb-4 ${
        type === 'success'
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}>
      {type === 'success' ? (
        <Check className='h-5 w-5' />
      ) : (
        <AlertCircle className='h-5 w-5' />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className='max-w-4xl mx-auto p-6'>
      <div className='flex flex-col mb-6'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-4 items-center'>
            <h1 className='font-bold text-3xl'>Hacker Check-In</h1>
          </div>
          <div className='flex items-center space-x-3'>
            {/* Auto Print Toggle */}
            <label className='flex items-center space-x-2 cursor-pointer select-none'>
              <Printer className='h-4 w-4 text-gray-500' />
              <span className='text-sm text-gray-600'>Auto Print</span>
              <button
                type='button'
                role='switch'
                aria-checked={autoPrint}
                onClick={() => {
                  setAutoPrint((prev) => {
                    autoPrintRef.current = !prev;
                    return !prev;
                  });
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                  autoPrint ? 'bg-blue-600' : 'bg-gray-300'
                }`}>
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    autoPrint ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
            <button
              onClick={() => {
                setScannedUserRecord(null);
                setScannedReturnedID(null);
              }}
              className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
              Clear
            </button>
            <ScanBadgeButton
              scannedReturnedID={scannedReturnedID}
              setScannedReturnedID={handleScannedID}
            />
          </div>
        </div>
      </div>

      {statusMessage && (
        <StatusMessage
          type={statusMessage.type}
          text={statusMessage.text}
        />
      )}

      <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
        {/* Hacker Information Card */}
        <div className='bg-white p-6 rounded-lg shadow'>
          <h2 className='font-semibold mb-4 text-xl'>Hacker Info</h2>
          <InfoItem
            icon={User}
            label='Preferred Name'
            value={
              scannedUserRecord?.first_name
                ? `${scannedUserRecord.first_name}${scannedUserRecord.last_name ? ` ${scannedUserRecord.last_name}` : ''}`
                : '...'
            }
          />
          <InfoItem
            icon={User}
            label='Full Name (Government)'
            value={scannedUserRecord?.application_info?.full_name}
          />
          <InfoItem
            icon={CalendarDays}
            label='T-Shirt'
            value={scannedUserRecord?.check_in_info?.tshirtSize}
          />
          <InfoItem
            icon={GraduationCap}
            label='University'
            value={scannedUserRecord?.application_info?.university}
          />
          <InfoItem
            icon={CalendarDays}
            label='Date of Birth'
            value={formatDate(scannedUserRecord?.application_info?.dob)}
          />
          <InfoItem
            icon={CalendarDays}
            label='Age'
            value={
              scannedUserRecord?.application_info?.dob
                ? `${calculateAge(
                    scannedUserRecord?.application_info?.dob
                  )} years`
                : '...'
            }
          />

          {/* Highlights Section */}
          {scannedUserRecord?.highlights && scannedUserRecord.highlights.length > 0 && (
            <div className='mt-6 pt-4 border-t border-gray-200'>
              <div className='flex items-center mb-3'>
                <Star className='h-5 w-5 text-yellow-500 mr-2' />
                <h3 className='font-semibold text-gray-900'>Highlights</h3>
              </div>
              <ul className='space-y-2'>
                {scannedUserRecord.highlights.map((highlight, index) => (
                  <li
                    key={index}
                    className='flex items-start space-x-2 text-sm text-gray-700'>
                    <span className='text-yellow-500 mt-1'>•</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Verification Card or Already Checked In Message */}
        <div className='bg-white p-6 rounded-lg shadow'>
          {scannedUserRecord?.check_in_info?.checkedIn ? (
            <div className='flex flex-col h-full items-center justify-center text-center'>
              <div className='bg-yellow-50 mb-4 p-4 rounded-full'>
                <AlertCircle className='h-12 text-yellow-500 w-12' />
              </div>
              <h2 className='font-semibold mb-2 text-2xl'>
                Already Checked In
              </h2>
              <p className='text-gray-600'>
                This hacker has already completed the check-in process.
              </p>
            </div>
          ) : (
            <>
              <h2 className='font-semibold mb-4 text-xl'>ID Verification</h2>

              {/* Government ID Section */}
              <div className='mb-6'>
                <div className='flex items-center mb-3'>
                  <div className='bg-blue-600 h-6 mr-2 rounded w-1'></div>
                  <h3 className='font-medium text-gray-900'>
                    Government ID Verification
                  </h3>
                </div>

                <div className='border-gray-100 border-l-2 pl-3'>
                  <p className='mb-3 text-red-600 text-sm'>
                    Government ID should be a legal ID like a driver's license
                    or passport.
                  </p>
                  <VerificationCheckbox
                    label='Name Verification'
                    checked={verificationState.nameVerified}
                    onChange={(e) =>
                      setVerificationState((prev) => ({
                        ...prev,
                        nameVerified: e.target.checked,
                      }))
                    }
                    instruction='Confirm name matches government ID'
                  />

                  <VerificationCheckbox
                    label='Date of Birth Verification'
                    checked={verificationState.dobVerified}
                    onChange={(e) =>
                      setVerificationState((prev) => ({
                        ...prev,
                        dobVerified: e.target.checked,
                      }))
                    }
                    instruction='Confirm DOB matches government ID'
                  />
                </div>
              </div>

              {/* University ID Section */}
              <div className='mb-4'>
                <div className='flex items-center mb-3'>
                  <div className='bg-green-600 h-6 mr-2 rounded w-1'></div>
                  <h3 className='font-medium text-gray-900'>
                    University ID Verification
                  </h3>
                </div>
                <div className='border-gray-100 border-l-2 pl-3'>
                  <VerificationCheckbox
                    label='University Verification'
                    checked={verificationState.universityVerified}
                    onChange={(e) =>
                      setVerificationState((prev) => ({
                        ...prev,
                        universityVerified: e.target.checked,
                      }))
                    }
                    instruction='Confirm university matches student ID'
                  />
                </div>
              </div>

              <button
                onClick={async () => {
                  setCheckInIsLoading(true);
                  const success = await checkInHackerHelper(
                    scannedReturnedID,
                    user,
                    setStatusMessage,
                    setCheckInIsLoading,
                    scannedUserRecord
                  );
                  if (success && autoPrintRef.current) {
                    // Use preferred name from API (already has fallback to Application name)
                    const firstName = scannedUserRecord?.first_name || '';
                    const lastName = scannedUserRecord?.last_name || '';
                    printBadge(firstName, lastName, scannedReturnedID);
                  }
                }}
                disabled={
                  !allVerified || scannedReturnedID === null || checkInIsLoading
                }
                className={`w-full py-3 px-4 rounded-lg font-medium text-white text-lg mt-4
                  ${
                    allVerified &&
                    scannedReturnedID !== null &&
                    !checkInIsLoading
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}>
                Check In
              </button>

              <p className='flex items-center mt-4 text-red-600 text-sm'>
                <AlertCircle className='h-5 inline mr-2 w-5' />
                If any info doesn't match their IDs, please direct the hacker to
                an exec on shift.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckIn;
